/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Center } from 'chakra-v3';
import { MdOutlineOpenInNew } from 'react-icons/md';
import { Text } from '../Text';
import { Link } from '../Link';

interface HelpWhatsAppLinkProps {
  whatsappWebUrl?: string;
}

/**
 * HelpWhatsAppLink component renders a link to open WhatsApp Web.
 * If no URL is provided, the component returns null and renders nothing.
 *
 * @param whatsappWebUrl - The WhatsApp Web URL to open in a new tab
 *
 * @example
 * ```tsx
 * <HelpWhatsAppLink whatsappWebUrl="https://web.whatsapp.com" />
 * ```
 */
const HelpWhatsAppLink: React.FC<HelpWhatsAppLinkProps> = ({ whatsappWebUrl }) => {
  if (!whatsappWebUrl) {
    return null;
  }

  return (
    <Center w="100%">
      <Link href={whatsappWebUrl} target="_blank" rel="noopener noreferrer" variant="whatsapp">
        <Text as="span" fontSize={{ base: '18px', md: '20px' }} fontWeight="600" color="brand.white">
          Go to{' '}
          <Text as="span" color="brand.secondaryBlue2">
            WhatsApp Web
          </Text>
        </Text>
        <Box flexShrink={0} color="brand.secondaryBlue2">
          <MdOutlineOpenInNew size={24} color="currentColor" aria-hidden="true" />
        </Box>
      </Link>
    </Center>
  );
};

export default HelpWhatsAppLink;
